import request from '../request';

/**
 * Service for managing payment plans
 * @namespace PaymentPlanService
 */

/**
 * Creates or updates a payment plan for the tenant
 * @param {Object} paymentPlanData - Payment plan data
 * @param {number} [paymentPlanData.paymentplanid] - Payment plan ID for updates
 * @param {string} paymentPlanData.applyto - Apply to (e.g., 'trip', 'booking')
 * @param {string} paymentPlanData.valueunit - Value unit (e.g., 'percentage', 'amount')
 * @param {number} paymentPlanData.value - Value amount
 * @param {number} paymentPlanData.finalpaymentdue - Final payment due (days)
 * @param {boolean} paymentPlanData.allowmonthlypayment - Allow monthly payments
 * @param {boolean} paymentPlanData.allowpartialpayment - Allow partial payments
 * @param {boolean} paymentPlanData.allowbookingwithoutpayment - Allow booking without payment
 * @param {number} [paymentPlanData.secondpaymentdue] - Second payment due (days)
 * @param {number} [paymentPlanData.secondpaymentamount] - Second payment amount
 * @param {number} [paymentPlanData.thirdpaymentdue] - Third payment due (days)
 * @param {number} [paymentPlanData.thirdpaymentamount] - Third payment amount
 * @param {number} [paymentPlanData.fourthpaymentdue] - Fourth payment due (days)
 * @param {number} [paymentPlanData.fourthpaymentamount] - Fourth payment amount
 * @param {string} [paymentPlanData.depositamount] - Deposit amount
 * @param {number|null} [tripId=null] - Trip ID for trip-specific plans, null for global plans
 * @returns {Promise<Object>} API response
 */
export const createOrUpdatePaymentPlan = (paymentPlanData, tripId = null) => {
  // Respect explicit tripid in payload, only override when a concrete tripId is provided
  const paymentPlan = {
    ...paymentPlanData
  };
  if (tripId !== null && tripId !== undefined) {
    paymentPlan.tripid = Number(tripId);
  }

  return request.post('paymentplan', paymentPlan).then((res) => res);
};

/**
 * Retrieves payment plan information for the current tenant
 * @returns {Promise<Object>} API response with payment plans
 */
export const getPaymentPlans = async () => {
  const response = await request({
    url: '/paymentplan',
    method: 'GET'
  });
  return response;
};

/**
 * Deletes a payment plan
 * @param {number} paymentPlanId - Payment plan ID to delete
 * @returns {Promise<Object>} API response
 */
export const deletePaymentPlan = async (paymentPlanId) => {
  const response = await request({
    url: `/paymentplan/${paymentPlanId}`,
    method: 'DELETE'
  });
  return response;
};

/**
 * Retrieves a payment plan for the current tenant
 * @param {number|null} [tripId=null] - Trip ID for trip-specific plans, null for global plans
 * @returns {Promise<Object>} API response with payment plan
 */
export const getPaymentPlan = async (tripId = null) => {
  const isTripContext = tripId !== null && tripId !== undefined;

  if (isTripContext) {
    // When a tripId is provided, fetch the trip and extract its payment plan
    const res = await request.get(`trips/${tripId}`);

    // Try different shapes that may contain the payment plan
    const tripPaymentPlan =
      res?.data?.paymentplan ||
      res?.data?.trip?.paymentplan ||
      res?.paymentplan ||
      res?.trip?.paymentplan ||
      null;

    if (!tripPaymentPlan) {
      // Signal to callers (like PaymentPlansPanel) that trip-level plan was not found
      throw new Error('Trip payment plan not found');
    }

    // Normalize to data.paymentplan for consumers
    return {
      ...res,
      data: {
        ...(res?.data || {}),
        paymentplan: tripPaymentPlan
      }
    };
  }

  // Global context: keep existing behavior
  const res = await request.get('paymentplan');
  if (res?.data?.paymentplan) {
    return {
      ...res,
      data: {
        ...res.data,
        paymentplan: res.data.paymentplan
      }
    };
  }
  return res;
};

/**
 * Retrieves the global payment plan for the current tenant
 * @returns {Promise<Object>} API response with global payment plan
 */
export const getGlobalPaymentPlan = () => {
  return getPaymentPlan(null);
};

/**
 * Filters global payment plans (where tripid is null)
 * @param {Array} paymentPlans - Array of payment plans
 * @returns {Array} Filtered global payment plans
 */
export const filterGlobalPaymentPlans = (paymentPlans) => {
  return paymentPlans?.filter((plan) => plan.tripid === null) || [];
};

/**
 * Validates payment plan data
 * @param {Object} paymentPlanData - Payment plan data to validate
 * @returns {Object} Validation result with errors
 */
export const validatePaymentPlan = (paymentPlanData) => {
  const errors = {};

  if (!paymentPlanData?.applyto) {
    errors.applyto = 'Apply to is required';
  }

  if (!paymentPlanData?.valueunit) {
    errors.valueunit = 'Value unit is required';
  }

  if (!paymentPlanData?.value || Number(paymentPlanData.value) <= 0) {
    errors.value = 'Value must be greater than 0';
  }

  if (
    !paymentPlanData?.finalpaymentdue ||
    Number(paymentPlanData.finalpaymentdue) <= 0
  ) {
    errors.finalpaymentdue = 'Final payment due must be greater than 0';
  }

  // Validate additional payment schedules if provided
  if (
    paymentPlanData?.secondpaymentdue &&
    (!paymentPlanData?.secondpaymentamount ||
      Number(paymentPlanData.secondpaymentamount) <= 0)
  ) {
    errors.secondpaymentamount =
      'Second payment amount is required when due date is set';
  }

  if (
    paymentPlanData?.thirdpaymentdue &&
    (!paymentPlanData?.thirdpaymentamount ||
      Number(paymentPlanData.thirdpaymentamount) <= 0)
  ) {
    errors.thirdpaymentamount =
      'Third payment amount is required when due date is set';
  }

  if (
    paymentPlanData?.fourthpaymentdue &&
    (!paymentPlanData?.fourthpaymentamount ||
      Number(paymentPlanData.fourthpaymentamount) <= 0)
  ) {
    errors.fourthpaymentamount =
      'Fourth payment amount is required when due date is set';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
